(() => {
  const STORAGE_KEY = 'heuif:cart-notice:hidden-until';
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const CHECKOUT_SELECTOR =
    '[data-cart-form] button[name="checkout"], [data-cart-form] input[name="checkout"], [data-additional-checkout-button] button, [data-additional-checkout-button] a, [data-additional-checkout-button] shopify-accelerated-checkout-cart';

  class CartNoticeModal extends HTMLElement {
    constructor() {
      super();

      this.isOpen = false;
      this.acknowledged = false;
      this.source = null;
      this.hideTimer = null;

      this.handleCartOpen = this.handleCartOpen.bind(this);
      this.handleCartClose = this.handleCartClose.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleModalMouseDown = this.handleModalMouseDown.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleSnooze = this.handleSnooze.bind(this);
    }

    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-notice-dialog]');
      this.closeButton = this.querySelector('[data-cart-notice-close]');
      this.snoozeButton = this.querySelector('[data-cart-notice-snooze]');

      this.acknowledged = this.isSnoozed();
      this.syncCheckoutState();

      this.closeButton?.addEventListener('click', this.handleClose);
      this.snoozeButton?.addEventListener('click', this.handleSnooze);
      this.addEventListener('mousedown', this.handleModalMouseDown);
      document.addEventListener('theme:cart:open', this.handleCartOpen);
      document.addEventListener('theme:cart:close', this.handleCartClose);
      document.addEventListener('click', this.handleDocumentClick, true);
      document.addEventListener('submit', this.handleDocumentSubmit, true);
      document.addEventListener('keydown', this.handleKeydown, true);

      requestAnimationFrame(() => {
        if (document.body.classList.contains('template-cart') && this.cartHasItems('page')) {
          this.requireAcknowledgement('page');
        }
      });
    }

    disconnectedCallback() {
      this.closeButton?.removeEventListener('click', this.handleClose);
      this.snoozeButton?.removeEventListener('click', this.handleSnooze);
      this.removeEventListener('mousedown', this.handleModalMouseDown);
      document.removeEventListener('theme:cart:open', this.handleCartOpen);
      document.removeEventListener('theme:cart:close', this.handleCartClose);
      document.removeEventListener('click', this.handleDocumentClick, true);
      document.removeEventListener('submit', this.handleDocumentSubmit, true);
      document.removeEventListener('keydown', this.handleKeydown, true);
    }

    isSnoozed() {
      try {
        const hiddenUntil = Number(window.localStorage.getItem(STORAGE_KEY));

        if (hiddenUntil > Date.now()) return true;
        if (hiddenUntil) window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // Storage can be unavailable in private browsing or restricted contexts.
      }

      return false;
    }

    cartHasItems(source) {
      const container = source === 'drawer' ? document.querySelector('[data-cart-drawer]') : document.querySelector('[data-cart-page]');
      return Boolean(container?.querySelector('[data-cart-item]'));
    }

    handleCartOpen() {
      requestAnimationFrame(() => {
        if (this.cartHasItems('drawer')) this.requireAcknowledgement('drawer');
      });
    }

    handleCartClose() {
      if (this.isOpen) this.hideModal(false);
    }

    handleModalMouseDown(event) {
      // The theme closes the cart drawer on document-level outside mousedown.
      // Keep interactions inside this global modal from reaching that handler.
      event.stopPropagation();
    }

    handleDocumentClick(event) {
      const checkoutControl = event.target.closest?.(CHECKOUT_SELECTOR);
      if (!checkoutControl || this.canCheckout()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const source = checkoutControl.closest('[data-cart-drawer]') ? 'drawer' : 'page';
      this.requireAcknowledgement(source);
    }

    handleDocumentSubmit(event) {
      if (!event.target.matches('[data-cart-form]') || this.canCheckout()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const source = event.target.closest('[data-cart-drawer]') ? 'drawer' : 'page';
      this.requireAcknowledgement(source);
    }

    handleKeydown(event) {
      if (!this.isOpen || event.code !== 'Escape') return;

      // The notice must be acknowledged with one of its visible controls.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeButton?.focus();
    }

    handleClose() {
      this.acknowledge();
    }

    handleSnooze() {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now() + DAY_IN_MS));
      } catch (error) {
        // Still acknowledge the notice for the current visit if storage fails.
      }

      this.acknowledge();
    }

    canCheckout() {
      if (this.isSnoozed()) {
        this.acknowledged = true;
        return true;
      }

      return this.acknowledged;
    }

    requireAcknowledgement(source) {
      if (this.canCheckout()) {
        this.syncCheckoutState();
        return;
      }

      this.source = source;
      this.acknowledged = false;
      this.syncCheckoutState();
      this.showModal();
    }

    acknowledge() {
      this.acknowledged = true;
      this.syncCheckoutState();
      this.hideModal(true);
    }

    syncCheckoutState() {
      const isLocked = !this.acknowledged && !this.isSnoozed();
      document.documentElement.classList.toggle('cart-notice-required', isLocked);

      document.querySelectorAll(CHECKOUT_SELECTOR).forEach((control) => {
        if (isLocked) {
          control.setAttribute('aria-disabled', 'true');
        } else {
          control.removeAttribute('aria-disabled');
        }
      });
    }

    showModal() {
      if (this.isOpen) {
        this.closeButton?.focus();
        return;
      }

      if (this.hideTimer) window.clearTimeout(this.hideTimer);
      this.isOpen = true;
      this.hidden = false;
      document.documentElement.classList.add('cart-notice-is-open');

      if (this.source === 'drawer') {
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        window.theme?.a11y?.removeTrapFocus({container: cartDrawer});
      } else {
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.dialog}));
      }

      requestAnimationFrame(() => {
        this.classList.add('is-open');
        window.theme?.a11y?.trapFocus({container: this.dialog, elementToFocus: this.closeButton});
      });
    }

    hideModal(restoreCartFocus) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cart-notice-is-open');
      window.theme?.a11y?.removeTrapFocus({container: this.dialog});

      if (this.source === 'drawer' && document.body.classList.contains('js-drawer-open-cart')) {
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        const checkoutButton = cartDrawer?.querySelector('button[name="checkout"]');
        window.theme?.a11y?.trapFocus({
          container: cartDrawer,
          elementToFocus: restoreCartFocus ? checkoutButton : undefined,
        });
      } else if (this.source === 'page') {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

        if (restoreCartFocus) {
          document.querySelector('[data-cart-page] button[name="checkout"]')?.focus();
        }
      }

      this.hideTimer = window.setTimeout(() => {
        if (!this.isOpen) this.hidden = true;
      }, 200);
    }
  }

  if (!customElements.get('cart-notice-modal')) {
    customElements.define('cart-notice-modal', CartNoticeModal);
  }
})();
